// Public site anonymous analytics tracker.
// - Persistent visitor_id in localStorage (opaque UUID, no PII).
// - Ephemeral session_id in sessionStorage (new per tab).
// - Fire-and-forget POST /api/public/track; failures are logged in development
//   only and never bubble up so tracking cannot break the visitor experience.
import { logger } from './logger';

const VISITOR_KEY = 'pg_visitor_id';
const SESSION_KEY = 'pg_session_id';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${BACKEND_URL}/api/public/track`;

const uid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `v-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
};

const getOrCreate = (storage, key) => {
  try {
    const existing = storage.getItem(key);
    if (existing) return existing;
    const fresh = uid();
    storage.setItem(key, fresh);
    return fresh;
  } catch (err) {
    // Storage may be blocked (private mode / disabled cookies). Continue in-memory.
    logger.warn('tracker: storage unavailable, using ephemeral id', err);
    return uid();
  }
};

const identity = () => {
  if (typeof window === 'undefined') return { visitor_id: uid(), session_id: uid() };
  const visitor_id = getOrCreate(window.localStorage, VISITOR_KEY);
  const session_id = getOrCreate(window.sessionStorage, SESSION_KEY);
  return { visitor_id, session_id };
};

const _send = (payload) => {
  try {
    const body = JSON.stringify(payload);
    // Prefer sendBeacon so nav-ending page_views still ship.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch((err) => logger.warn('tracker: fetch failed', err));
  } catch (err) {
    // Never bubble a tracker exception into the site's runtime.
    logger.warn('tracker: send failed', err);
  }
};

const _base = (extra = {}) => {
  const { visitor_id, session_id } = identity();
  const path = typeof window !== 'undefined' ? window.location.pathname + window.location.hash : '/';
  const referrer = typeof document !== 'undefined' ? document.referrer || '' : '';
  const screen_w = typeof window !== 'undefined' ? window.innerWidth || 0 : 0;
  const screen_h = typeof window !== 'undefined' ? window.innerHeight || 0 : 0;
  const tz_offset = new Date().getTimezoneOffset();
  let timezone = '';
  try {
    // IANA timezone name (e.g. "Asia/Riyadh"). Lets the backend infer country
    // reliably even when infra doesn't set cf-ipcountry / x-country-code.
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    timezone = '';
  }
  const language = typeof navigator !== 'undefined' ? (navigator.language || '') : '';
  return { visitor_id, session_id, path, referrer, screen_w, screen_h, tz_offset, timezone, language, ...extra };
};

export const trackPageView = (pathOverride) => {
  const payload = { event: 'page_view', ..._base({}) };
  if (pathOverride) payload.path = pathOverride;
  _send(payload);
};

export const trackFormStart = (form) => {
  if (!form) return;
  _send({ event: 'form_start', ..._base({ form }) });
};

export const trackFormSubmit = (form) => {
  if (!form) return;
  _send({ event: 'form_submit', ..._base({ form }) });
};

// Convenience initializer: ensures visitor + session ids exist and fires the
// initial page view. Safe to call multiple times.
export const initTracker = () => {
  identity();
  trackPageView();
};
